/**
 * Handshake loading UI — matches authentication-ui `FullScreenLoader` + `Spinner`
 * (`size="lg"`) exactly so the PPUI → Auth handoff does not flicker.
 *
 * Auth source:
 *   authentication-ui/src/lib/components/FullScreenLoader.tsx
 *   authentication-ui/src/lib/components/ui/spinner.tsx
 *
 * If `#hs-boot-shell` already painted the same spinner in index.html, keep that
 * shell visible (restarting the CSS animation on remount would look janky).
 */
function hasHandshakeBootShell(): boolean {
  return (
    typeof document !== "undefined" &&
    document.getElementById("hs-boot-shell") != null
  );
}

/** Auth `Spinner` size `lg`: w-9 h-9 border-4. */
function AuthSpinner(): React.JSX.Element {
  return (
    <div
      className="animate-spin rounded-full border-solid border-gray-100 border-t-gray-900 w-9 h-9 border-4"
      role="status"
      aria-label="Loading"
      data-testid="spinner"
    />
  );
}

/**
 * Auth `FullScreenLoader` — fixed white overlay, centered lg spinner.
 */
export function WelcomeScreen(): React.JSX.Element {
  if (hasHandshakeBootShell()) {
    // Boot shell already exposes data-testid="handshake-welcome-screen".
    return <div className="min-h-screen" data-boot-shell="true" aria-busy="true" />;
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-white z-50"
      role="status"
      aria-label="Loading"
      data-testid="handshake-welcome-screen"
    >
      <div className="flex flex-col items-center gap-4">
        <AuthSpinner />
      </div>
    </div>
  );
}
